// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import {
  ACTIVITY_FILTER_DEFAULTS,
  type ActivityEntry,
  type ActivityFilterState,
  ActivityTimeline,
} from "@/components/shared/activity-timeline";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";

// NOTE: activity data is mocked — this tab is debug-only until the backend exposes a real feed.
function generateMockData(): ActivityEntry[] {
  const now = Date.now();
  const day = 86_400_000;
  return [
    {
      id: 1,
      timestamp: new Date(now - 0.2 * day).toISOString(),
      user: "ops@focal.systems",
      action: "updated",
      target: "Predicate",
      message: "Narrowed scope to bake-off in the morning: added && time.current_hour < 11.",
    },
    {
      id: 2,
      timestamp: new Date(now - 1 * day).toISOString(),
      user: "admin@focal.systems",
      action: "updated",
      target: "Rule",
      field: "priority",
      oldValue: "50",
      newValue: "100",
    },
    {
      id: 3,
      timestamp: new Date(now - 3 * day).toISOString(),
      user: "ops@focal.systems",
      action: "updated",
      target: "Rule",
      field: "outcome",
      oldValue: "allow",
      newValue: "deny",
    },
    {
      id: 4,
      timestamp: new Date(now - 6 * day).toISOString(),
      user: "lukasz@focal.systems",
      action: "deactivated",
      target: "Rule",
      message: "Paused while validating the new visibility window with the field team.",
    },
    {
      id: 5,
      timestamp: new Date(now - 12 * day).toISOString(),
      user: "admin@focal.systems",
      action: "updated",
      target: "Rule",
      field: "endDate",
      oldValue: "—",
      newValue: "2026-03-31",
    },
    {
      id: 6,
      timestamp: new Date(now - 21 * day).toISOString(),
      user: "ops@focal.systems",
      action: "created",
      target: "Rule",
    },
  ];
}

const MOCK_DATA = generateMockData();

interface OctoRuleActivityTabProps {
  octoRuleId: number;
  initialFilters?: string;
}

export function OctoRuleActivityTab({ octoRuleId, initialFilters }: OctoRuleActivityTabProps) {
  const navigate = useNavigate();
  const initial = useMemo(() => decodeFilters(initialFilters, ACTIVITY_FILTER_DEFAULTS), [initialFilters]);

  const handleFilterChange = useCallback(
    (values: ActivityFilterState) => {
      const encoded = encodeFilters(values, ACTIVITY_FILTER_DEFAULTS);
      navigate({
        to: "/octo/rules/$ruleId",
        params: { ruleId: String(octoRuleId) },
        search: { t: "activity", ...(encoded ? { f: encoded } : {}) },
        replace: true,
      });
    },
    [navigate, octoRuleId],
  );

  return <ActivityTimeline entries={MOCK_DATA} initialFilters={initial} onFilterChange={handleFilterChange} />;
}
